import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import Recaptcha from '@/components/Recaptcha';
import { useRecaptcha } from '@/hooks/useRecaptcha';

export class RecaptchaNotResolvedError extends Error {
    constructor() {
        super('reCAPTCHA not resolved');
        this.name = 'RecaptchaNotResolvedError';
    }
}

export const isRecaptchaNotResolved = (error: unknown) =>
    error instanceof RecaptchaNotResolvedError;

type RecaptchaContextValue = {
    register: () => () => void;
    getRecaptchaToken: () => Promise<string | null>;
};

const RecaptchaContext = createContext<RecaptchaContextValue | null>(null);

export function RecaptchaProvider({ children }: { children: React.ReactNode }) {
    const { recaptchaRef, getRecaptchaToken, cancelRecaptcha } = useRecaptcha();
    const [consumers, setConsumers] = useState(0);

    const register = useCallback(() => {
        setConsumers((count) => count + 1);
        return () => setConsumers((count) => count - 1);
    }, []);

    useEffect(() => {
        if (consumers === 0) cancelRecaptcha();
    }, [consumers, cancelRecaptcha]);

    const queueRef = useRef<Promise<unknown>>(Promise.resolve());

    const getQueuedRecaptchaToken = useCallback(() => {
        const next = queueRef.current.then(getRecaptchaToken);
        queueRef.current = next.catch(() => null);
        return next;
    }, [getRecaptchaToken]);

    const value = useMemo(
        () => ({ register, getRecaptchaToken: getQueuedRecaptchaToken }),
        [register, getQueuedRecaptchaToken],
    );

    return (
        <RecaptchaContext.Provider value={value}>
            {children}
            {consumers > 0 && <Recaptcha ref={recaptchaRef} />}
        </RecaptchaContext.Provider>
    );
}

export const useRequiredRecaptchaToken = () => {
    const context = useContext(RecaptchaContext);
    if (!context) {
        throw new Error(
            'useRequiredRecaptchaToken must be used within RecaptchaProvider',
        );
    }

    const { register, getRecaptchaToken } = context;

    useEffect(() => register(), [register]);

    return useCallback(async () => {
        const token = await getRecaptchaToken();
        if (!token) throw new RecaptchaNotResolvedError();
        return token;
    }, [getRecaptchaToken]);
};
